import { FormattedMessage, useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useMutation } from "@tanstack/react-query";

import { ReactComponent as Triangle } from "assets/images/iconsV2/triangle-alert.svg";
import Button from "components/v2/Button/Button";
import Modal from "components/v2/Modal/Modal";
import { regenerateTruckTrackingLink } from "api/services/shipment";
import errorToast from "helpers/errorToast";
import { useGetTrackingReferences } from "api/queries/shipments/useGetTrackingReferences";

interface RegenerateLinkModalProps {
  show: boolean;
  onHide: () => void;
  trackingId?: number;
}

export default function RegenerateLinkModal({ show, onHide, trackingId }: RegenerateLinkModalProps) {
  const intl = useIntl();
  const { id } = useParams();

  const { refetch } = useGetTrackingReferences(id!, {
    enabled: !!id,
    refetchOnMount: false,
  });

  const { mutate, isLoading } = useMutation({
    mutationFn: () => regenerateTruckTrackingLink(id, trackingId),
    onSuccess: () => {
      refetch();
      onHide();
      toast(
        <FormattedMessage id="tracking.truckRegenerateLinkSuccessMessage" defaultMessage="Link successfully regenerated" />,
        { type: "success" },
      );
    },
    onError: () =>
      errorToast(
        <FormattedMessage
          id="tracking.truckRegenerateLinkErrorMessage"
          defaultMessage="Could not regenerate link. Please try again later."
        />,
      ),
  });

  const handleConfirm = () => {
    mutate();
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header>
        <div className="d-flex gap-lg">
          <div
            style={{ width: 48, height: 48 }}
            className="d-flex justify-content-center align-items-center p-lg shadow-xs radius-full bg-warning-secondary"
          >
            <Triangle />
          </div>
          <div className="mt-md">
            <div className="text-primary text-xl font-semibold">
              <FormattedMessage id="tracking.truckRegenerateLink" defaultMessage="Regenerate Link" />
            </div>

            <div className="mt-xl text-tertiary text-sm">
              <div>
                <FormattedMessage
                  id="tracking.truckRegenerateLinkSubheader1"
                  defaultMessage="Regenerating the link will <b>invalidate the existing link</b> and <b>stop the existing location tracking.</b>"
                  values={{
                    b: (chunks) => <strong>{chunks}</strong>,
                  }}
                />
              </div>

              <div className="mt-md">
                <FormattedMessage
                  id="tracking.truckRegenerateLinkSubheader2"
                  defaultMessage="Only proceed if you intend to replace the link or send it to a different driver."
                />
              </div>
            </div>
          </div>
        </div>
      </Modal.Header>

      <Modal.Footer>
        <div className="d-flex align-items-center justify-content-end gap-3xl">
          <Button
            label={intl.formatMessage({
              id: "general.cancel",
              defaultMessage: "Cancel",
            })}
            variant="link-color"
            onClick={onHide}
          />
          <Button
            label={intl.formatMessage({
              id: "tracking.truckRegenerateLink",
              defaultMessage: "Regenerate link",
            })}
            disabled={isLoading}
            onClick={handleConfirm}
          />
        </div>
      </Modal.Footer>
    </Modal>
  );
}

RegenerateLinkModal.defaultProps = {
  trackingId: undefined,
};
